import { decode } from './decode'
import { detectHeader } from './detectHeader'
import { detectSkipRows } from './detectSkipRows'
import { detectTrailingRows } from './detectTrailingRows'
import { MAX_IMPORT_ROWS } from './fields'
import { readCsv } from './readCsv'
import { readXlsx } from './readXlsx'
import type { Encoding, ParsedTable } from './types'

export * from './types'
export { FIELDS, FIELD_KEYS, REQUIRED_FIELD_KEYS, fieldByKey, MAX_IMPORT_ROWS } from './fields'
export { suggestMapping, suggestionsToMapping, SUGGESTION_THRESHOLD } from './suggestMapping'
export {
    applyMapping,
    rowsToJson,
    missingRequiredFields,
    validateFixedValue,
    FIELD_LABELS
} from './applyMapping'
export { detectHeader } from './detectHeader'
export { detectTrailingRows } from './detectTrailingRows'

/** Chyba, kterou má smysl ukázat uživateli beze změny. */
export class ImportFileError extends Error {}

export type ParseOptions = {
    /** Ruční přepis detekce — uživatel může každý odhad opravit. */
    encoding?: Encoding
    delimiter?: string
    hasHeader?: boolean
    sheet?: string
    /**
     * Kolik řádků na začátku zahodit ještě PŘED detekcí hlavičky.
     *
     * Exporty občas začínají titulkem nebo poznámkou („Přístupové kódy“), pod
     * kterou teprve následuje skutečná hlavička. Bez tohohle by se za hlavičku
     * považoval titulek.
     */
    skipRows?: number
    /**
     * Kolik řádků na konci zahodit — souhrny („CELKEM“), poznámky.
     *
     * Bez toho projde souhrn jako vstupenka s kódem „CELKEM“.
     */
    skipTrailingRows?: number
}

const XLSX_EXTENSIONS = ['.xlsx', '.xls', '.xlsm']

export function isSupportedFile(name: string): boolean {
    const lower = name.toLowerCase()
    return lower.endsWith('.csv') || XLSX_EXTENSIONS.some(e => lower.endsWith(e))
}

/**
 * Soubor → tabulka připravená k mapování.
 *
 * Sjednocuje CSV i XLSX do jednoho tvaru; volající už formát neřeší.
 */
export async function parseFile(file: File, opts: ParseOptions = {}): Promise<ParsedTable> {
    const buffer = await file.arrayBuffer()
    const isXlsx = XLSX_EXTENSIONS.some(e => file.name.toLowerCase().endsWith(e))

    let rows: string[][]
    let encoding: Encoding = 'utf-8'
    let delimiter: string | null = null
    let sheetNames: string[] | null = null
    let activeSheet: string | null = null

    if (isXlsx) {
        const result = await readXlsx(buffer, opts.sheet)
        rows = result.rows
        sheetNames = result.sheetNames
        activeSheet = result.activeSheet
    } else {
        const decoded = decode(buffer, opts.encoding)
        encoding = decoded.encoding
        const result = readCsv(decoded.text, opts.delimiter)
        rows = result.rows
        delimiter = result.delimiter
    }

    if (rows.length === 0) {
        throw new ImportFileError('Soubor neobsahuje žádná data')
    }

    // Odříznout úvodní řádky ještě před detekcí hlavičky — jinak by se za
    // hlavičku považoval titulek nad ní. Ruční volba vždy přebíjí odhad.
    const skipRows = Math.max(0, Math.min(opts.skipRows ?? detectSkipRows(rows), rows.length))
    const body = rows.slice(skipRows)

    if (body.length === 0) {
        throw new ImportFileError(`Po přeskočení ${skipRows} řádků nezbyla žádná data`)
    }

    const detected = detectHeader(body)
    const hasHeader = opts.hasHeader ?? detected.hasHeader

    const headers = hasHeader ? body[0] : null
    const afterHeader = hasHeader ? body.slice(1) : body

    // Ořez konce až po odečtení hlavičky, aby se detekce dívala na skutečná
    // data. Ruční volba opět přebíjí odhad.
    const skipTrailingRows = Math.max(
        0,
        Math.min(opts.skipTrailingRows ?? detectTrailingRows(afterHeader), afterHeader.length)
    )
    const dataRows = skipTrailingRows > 0 ? afterHeader.slice(0, -skipTrailingRows) : afterHeader

    if (dataRows.length === 0) {
        // Rozlišit, co za prázdnotu může — jinak dostane uživatel matoucí
        // hlášku „po odříznutí 0 řádků" u souboru, kde je jen hlavička.
        throw new ImportFileError(
            skipTrailingRows > 0
                ? `Po odříznutí ${skipTrailingRows} řádků na konci nezbyla žádná data`
                : 'Soubor obsahuje jen hlavičku, žádná data'
        )
    }

    // Limit se hlídá tady, aby se uživatel k mapování vůbec nedostal —
    // backend ho vynucuje znovu, přímé volání API ho neobejde.
    if (dataRows.length > MAX_IMPORT_ROWS) {
        throw new ImportFileError(
            `Soubor má ${dataRows.length} řádků, maximum je ${MAX_IMPORT_ROWS}. ` +
                `Rozdělte zdroj na více souborů.`
        )
    }

    return {
        rows: dataRows,
        headers,
        hasHeader,
        // Ruční volba je jistota; jinak platí, jak si byla detekce jistá.
        headerConfidence: opts.hasHeader === undefined ? detected.confidence : 'high',
        encoding,
        delimiter,
        sheetNames,
        activeSheet,
        skipRows,
        skipTrailingRows,
        rawPreview: rows.slice(0, skipRows + 8),
        // Konec souboru včetně případných odříznutých řádků. Zobrazuje se vždy,
        // ať je vidět, čím soubor doopravdy končí — i když se nic neořezává.
        rawTailPreview: rows.slice(-(skipTrailingRows + 3)),
        // Číslo prvního datového řádku ve ZDROJOVÉM souboru — musí počítat
        // i s přeskočenými řádky, jinak by chyby ukazovaly jinam.
        firstDataRow: skipRows + (hasHeader ? 2 : 1),
        // Celkový počet řádků ve zdroji — kvůli číslování v ukázce konce.
        totalSourceRows: rows.length
    }
}
